import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import { Loader2, Network, Pencil, ShieldCheck, Trash2, X } from 'lucide-react';

interface SkillOption {
  id: string;
  name: string;
}

interface AdapterOption {
  sourceKey: string;
  type: string;
}

interface BridgeOption {
  bridgeCode: string;
  label: string;
}

interface PolicyOverview {
  bindings: Array<Record<string, unknown>>;
  skillPolicies: Array<Record<string, unknown>>;
  connectorPolicies: Array<Record<string, unknown>>;
}

type ConnectorKind = 'adapter' | 'bridge';

const CMS_PRO2_RESPONSE_POLICY_TEMPLATE = {
  responsePolicyVersion: 1,
  simpleMetrics: {
    today_job_files_created: {
      summaryChoices: [
        { action: 'type_mix', label: '看类型占比' },
        { action: 'latest_jobs', label: '看最新 Job File 清单' },
        { action: 'import_export_share', label: '看 import/export 比例' },
      ],
    },
    import_nomination_volume_period: {
      summaryChoices: [
        { action: 'by_agent', label: '按 agent 拆解 volume' },
        { action: 'monthly_trend', label: '看月度 nomination 趋势' },
        { action: 'definition', label: '看口径与定义说明' },
      ],
    },
  },
  diagnostics: {
    freehand_vs_nomination_last_3m: {
      summaryChoices: [
        { action: 'monthly_table', label: '看月度结构表' },
        { action: 'margin_compare', label: '看毛利率对比' },
        { action: 'signals', label: '看异常信号总结' },
      ],
    },
    agent_hbl_margin_diagnostics: {
      summaryChoices: [
        { action: 'risk_table', label: '看高风险 agent 表' },
        { action: 'reason_breakdown', label: '看成因拆解' },
        { action: 'zero_revenue', label: '看零收入风险' },
      ],
    },
    lane_cbm_profit_divergence: {
      summaryChoices: [
        { action: 'divergence_table', label: '看量涨利跌 lane 表' },
        { action: 'delta_compare', label: '看涨跌幅对比' },
        { action: 'weakest_rank', label: '看最弱 lane 排名' },
      ],
    },
    high_revenue_low_margin_density: {
      summaryChoices: [
        { action: 'combo_table', label: '看高营收低毛利组合表' },
        { action: 'revenue_net_compare', label: '看营收净利对比' },
        { action: 'weakest_rank', label: '看最弱组合排名' },
      ],
    },
    port_volume_quality_risk: {
      summaryChoices: [
        { action: 'risk_table', label: '看高风险港口表' },
        { action: 'ts_nomination_breakdown', label: '看 TS / nomination 拆解' },
        { action: 'weakest_rank', label: '看最弱港口排名' },
      ],
    },
    import_nomination_ts_margin_trend_last_3m: {
      summaryChoices: [
        { action: 'monthly_table', label: '看月度 nomination 趋势表' },
        { action: 'ts_share', label: '看 TS nomination 占比' },
        { action: 'margin_signal', label: '看利润质量信号' },
      ],
    },
  },
} as const;

function asString(value: unknown, fallback = '—') {
  if (typeof value === 'string' && value.length > 0) return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return fallback;
}

function parseJsonSafe(value: string) {
  if (!value.trim()) return {};
  return JSON.parse(value) as Record<string, unknown>;
}

export default function TenantPolicySetupPage() {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState<string | null>(null);
  const [skills, setSkills] = useState<SkillOption[]>([]);
  const [adapters, setAdapters] = useState<AdapterOption[]>([]);
  const [bridges, setBridges] = useState<BridgeOption[]>([]);
  const [overview, setOverview] = useState<PolicyOverview>({
    bindings: [],
    skillPolicies: [],
    connectorPolicies: [],
  });

  const [bindingSkillId, setBindingSkillId] = useState('');
  const [bindingConnectorKind, setBindingConnectorKind] = useState<ConnectorKind>('bridge');
  const [bindingConnectorRef, setBindingConnectorRef] = useState('');
  const [bindingRole, setBindingRole] = useState('primary');
  const [bindingPriority, setBindingPriority] = useState('100');
  const [bindingConfig, setBindingConfig] = useState('{}');
  const [editingBindingId, setEditingBindingId] = useState<string | null>(null);

  const [skillPolicySkillId, setSkillPolicySkillId] = useState('');
  const [skillPolicyScope, setSkillPolicyScope] = useState('tenant');
  const [skillPolicyJson, setSkillPolicyJson] = useState('{\n  "readOnly": true\n}');
  const [skillPolicyNotes, setSkillPolicyNotes] = useState('');
  const [editingSkillPolicyId, setEditingSkillPolicyId] = useState<string | null>(null);

  const [connectorPolicyKind, setConnectorPolicyKind] = useState<ConnectorKind>('bridge');
  const [connectorPolicyRef, setConnectorPolicyRef] = useState('');
  const [connectorPolicyCode, setConnectorPolicyCode] = useState('prepared_required');
  const [connectorPolicyJson, setConnectorPolicyJson] = useState('{\n  "enabled": true\n}');
  const [connectorPolicyNotes, setConnectorPolicyNotes] = useState('');
  const [editingConnectorPolicyId, setEditingConnectorPolicyId] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [skillsRes, adaptersRes, bridgesRes, overviewRes] = await Promise.all([
        api.get<{ items: Array<{ id: string; name: string }> }>('/skills?limit=100'),
        api.get<{ items: Array<{ sourceKey: string; type: string }> }>('/tenant-adapters'),
        api.get<{ items: Array<{ bridgeCode: string; label: string }> }>('/tenant-bridges'),
        api.get<PolicyOverview>('/tenant-skill-policies/overview'),
      ]);

      setSkills(skillsRes.items ?? []);
      setAdapters(adaptersRes.items ?? []);
      setBridges(bridgesRes.items ?? []);
      setOverview(overviewRes);

      const firstSkillId = skillsRes.items?.[0]?.id ?? '';
      const firstBridgeCode = bridgesRes.items?.[0]?.bridgeCode ?? '';

      if (firstSkillId) {
        setBindingSkillId((prev) => prev || firstSkillId);
        setSkillPolicySkillId((prev) => prev || firstSkillId);
      }
      if (firstBridgeCode) {
        setBindingConnectorRef((prev) => prev || firstBridgeCode);
        setConnectorPolicyRef((prev) => prev || firstBridgeCode);
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to load policy setup');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  useEffect(() => {
    const next = bindingConnectorKind === 'bridge'
      ? bridges[0]?.bridgeCode
      : adapters[0]?.sourceKey;
    if (next) setBindingConnectorRef(next);
  }, [bindingConnectorKind, bridges, adapters]);

  useEffect(() => {
    const next = connectorPolicyKind === 'bridge'
      ? bridges[0]?.bridgeCode
      : adapters[0]?.sourceKey;
    if (next) setConnectorPolicyRef(next);
  }, [connectorPolicyKind, bridges, adapters]);

  const connectorOptions = bindingConnectorKind === 'bridge'
    ? bridges.map((bridge) => ({ value: bridge.bridgeCode, label: `${bridge.label} (${bridge.bridgeCode})` }))
    : adapters.map((adapter) => ({ value: adapter.sourceKey, label: `${adapter.sourceKey} (${adapter.type})` }));

  const connectorPolicyOptions = connectorPolicyKind === 'bridge'
    ? bridges.map((bridge) => ({ value: bridge.bridgeCode, label: `${bridge.label} (${bridge.bridgeCode})` }))
    : adapters.map((adapter) => ({ value: adapter.sourceKey, label: `${adapter.sourceKey} (${adapter.type})` }));

  const runAction = async (key: string, fn: () => Promise<void>) => {
    setSubmitting(key);
    try {
      await fn();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Action failed');
    } finally {
      setSubmitting(null);
    }
  };

  const resetBindingForm = () => {
    setEditingBindingId(null);
    setBindingRole('primary');
    setBindingPriority('100');
    setBindingConfig('{}');
  };

  const resetSkillPolicyForm = () => {
    setEditingSkillPolicyId(null);
    setSkillPolicyScope('tenant');
    setSkillPolicyJson('{\n  "readOnly": true\n}');
    setSkillPolicyNotes('');
  };

  const loadCmsPro2StarterPolicy = () => {
    const cmsPro2Skill = skills.find((skill) => skill.id === 'cms-pro2-query');
    if (cmsPro2Skill) setSkillPolicySkillId(cmsPro2Skill.id);
    setSkillPolicyScope('cms_pro2_response');
    setSkillPolicyJson(JSON.stringify(CMS_PRO2_RESPONSE_POLICY_TEMPLATE, null, 2));
    setSkillPolicyNotes('CMS PRO2 response policy starter');
    setEditingSkillPolicyId(null);
  };

  const resetConnectorPolicyForm = () => {
    setEditingConnectorPolicyId(null);
    setConnectorPolicyCode('prepared_required');
    setConnectorPolicyJson('{\n  "enabled": true\n}');
    setConnectorPolicyNotes('');
  };

  const createBinding = async () => {
    await runAction('binding', async () => {
      const payload = {
        skillId: bindingSkillId,
        connectorKind: bindingConnectorKind,
        connectorRef: bindingConnectorRef,
        bindingRole,
        priority: Number(bindingPriority || '100'),
        enabled: true,
        config: parseJsonSafe(bindingConfig),
      };
      if (editingBindingId) {
        await api.patch(`/tenant-skill-policies/bindings/${editingBindingId}`, payload);
      } else {
        await api.post('/tenant-skill-policies/bindings', payload);
      }
      toast.success(editingBindingId ? 'Skill binding updated' : 'Skill binding saved');
      resetBindingForm();
      await loadData();
    });
  };

  const createSkillPolicy = async () => {
    await runAction('skill-policy', async () => {
      const payload = {
        skillId: skillPolicySkillId,
        policyScope: skillPolicyScope,
        status: 'active',
        versionNo: 1,
        policyJson: parseJsonSafe(skillPolicyJson),
        notes: skillPolicyNotes.trim() || null,
      };
      if (editingSkillPolicyId) {
        await api.patch(`/tenant-skill-policies/skill-policies/${editingSkillPolicyId}`, payload);
      } else {
        await api.post('/tenant-skill-policies/skill-policies', payload);
      }
      toast.success(editingSkillPolicyId ? 'Skill policy updated' : 'Skill policy saved');
      resetSkillPolicyForm();
      await loadData();
    });
  };

  const createConnectorPolicy = async () => {
    await runAction('connector-policy', async () => {
      const payload = {
        connectorKind: connectorPolicyKind,
        connectorRef: connectorPolicyRef,
        policyCode: connectorPolicyCode,
        status: 'active',
        policyJson: parseJsonSafe(connectorPolicyJson),
        notes: connectorPolicyNotes.trim() || null,
      };
      if (editingConnectorPolicyId) {
        await api.patch(`/tenant-skill-policies/connector-policies/${editingConnectorPolicyId}`, payload);
      } else {
        await api.post('/tenant-skill-policies/connector-policies', payload);
      }
      toast.success(editingConnectorPolicyId ? 'Connector policy updated' : 'Connector policy saved');
      resetConnectorPolicyForm();
      await loadData();
    });
  };

  const deleteRecord = async (kind: 'bindings' | 'skill-policies' | 'connector-policies', id: string) => {
    await runAction(`delete-${id}`, async () => {
      await api.delete(`/tenant-skill-policies/${kind}/${id}`);
      toast.success('Deleted');
      if (kind === 'bindings' && editingBindingId === id) resetBindingForm();
      if (kind === 'skill-policies' && editingSkillPolicyId === id) resetSkillPolicyForm();
      if (kind === 'connector-policies' && editingConnectorPolicyId === id) resetConnectorPolicyForm();
      await loadData();
    });
  };

  const startEditBinding = (row: Record<string, unknown>) => {
    setEditingBindingId(asString(row['id'], ''));
    setBindingSkillId(asString(row['skill_id'], ''));
    setBindingConnectorKind(asString(row['connector_kind'], 'bridge') as ConnectorKind);
    setBindingConnectorRef(asString(row['connector_ref'], ''));
    setBindingRole(asString(row['binding_role'], 'primary'));
    setBindingPriority(asString(row['priority'], '100'));
    setBindingConfig(JSON.stringify((row['config'] as Record<string, unknown>) ?? {}, null, 2));
  };

  const startEditSkillPolicy = (row: Record<string, unknown>) => {
    setEditingSkillPolicyId(asString(row['id'], ''));
    setSkillPolicySkillId(asString(row['skill_id'], ''));
    setSkillPolicyScope(asString(row['policy_scope'], 'tenant'));
    setSkillPolicyJson(JSON.stringify((row['policy_json'] as Record<string, unknown>) ?? {}, null, 2));
    setSkillPolicyNotes(asString(row['notes'], ''));
  };

  const startEditConnectorPolicy = (row: Record<string, unknown>) => {
    setEditingConnectorPolicyId(asString(row['id'], ''));
    setConnectorPolicyKind(asString(row['connector_kind'], 'bridge') as ConnectorKind);
    setConnectorPolicyRef(asString(row['connector_ref'], ''));
    setConnectorPolicyCode(asString(row['policy_code'], 'prepared_required'));
    setConnectorPolicyJson(JSON.stringify((row['policy_json'] as Record<string, unknown>) ?? {}, null, 2));
    setConnectorPolicyNotes(asString(row['notes'], ''));
  };

  if (loading) {
    return (
      <div className="flex h-[60vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-bold">
          <ShieldCheck className="h-6 w-6 text-emerald-600" />
          Tenant Skill Policy Setup
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Bind tenant skills to connectors and define tenant-specific skill and connector policies.
        </p>
      </div>

      <div className="grid gap-6 xl:grid-cols-3">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between gap-3">
              <CardTitle className="text-base">Skill Binding</CardTitle>
              {editingBindingId && (
                <Button variant="ghost" size="sm" onClick={resetBindingForm}>
                  <X className="mr-2 h-4 w-4" />
                  Cancel Edit
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <Field label="Skill">
              <Select value={bindingSkillId} onValueChange={setBindingSkillId}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {skills.map((skill) => (
                    <SelectItem key={skill.id} value={skill.id}>{skill.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Connector Kind">
              <Select value={bindingConnectorKind} onValueChange={(value) => setBindingConnectorKind(value as ConnectorKind)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="bridge">bridge</SelectItem>
                  <SelectItem value="adapter">adapter</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Connector Ref">
              <Select value={bindingConnectorRef} onValueChange={setBindingConnectorRef}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {connectorOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Binding Role">
              <Input value={bindingRole} onChange={(e) => setBindingRole(e.target.value)} />
            </Field>
            <Field label="Priority">
              <Input value={bindingPriority} onChange={(e) => setBindingPriority(e.target.value)} />
            </Field>
            <Field label="Config JSON">
              <Textarea value={bindingConfig} onChange={(e) => setBindingConfig(e.target.value)} rows={6} />
            </Field>
            <Button onClick={() => void createBinding()} disabled={submitting === 'binding'}>
              {submitting === 'binding' && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {editingBindingId ? 'Update Binding' : 'Save Binding'}
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between gap-3">
              <CardTitle className="text-base">Skill Policy</CardTitle>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={loadCmsPro2StarterPolicy}>
                  Load CMS PRO2 Starter
                </Button>
                {editingSkillPolicyId && (
                  <Button variant="ghost" size="sm" onClick={resetSkillPolicyForm}>
                    <X className="mr-2 h-4 w-4" />
                    Cancel Edit
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <Field label="Skill">
              <Select value={skillPolicySkillId} onValueChange={setSkillPolicySkillId}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {skills.map((skill) => (
                    <SelectItem key={skill.id} value={skill.id}>{skill.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Policy Scope">
              <Input value={skillPolicyScope} onChange={(e) => setSkillPolicyScope(e.target.value)} />
            </Field>
            <Field label="Policy JSON">
              <Textarea value={skillPolicyJson} onChange={(e) => setSkillPolicyJson(e.target.value)} rows={8} />
            </Field>
            <p className="text-xs text-muted-foreground">
              CMS PRO2 建议用 `skillId=cms-pro2-query` + `policyScope=cms_pro2_response`。
              这类 policy 用来热更新 summary 的 1/2/3 选项和 follow-up 含义，不需要再改代码文案。
            </p>
            <Field label="Notes">
              <Input value={skillPolicyNotes} onChange={(e) => setSkillPolicyNotes(e.target.value)} />
            </Field>
            <Button onClick={() => void createSkillPolicy()} disabled={submitting === 'skill-policy'}>
              {submitting === 'skill-policy' && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {editingSkillPolicyId ? 'Update Skill Policy' : 'Save Skill Policy'}
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between gap-3">
              <CardTitle className="text-base">Connector Policy</CardTitle>
              {editingConnectorPolicyId && (
                <Button variant="ghost" size="sm" onClick={resetConnectorPolicyForm}>
                  <X className="mr-2 h-4 w-4" />
                  Cancel Edit
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <Field label="Connector Kind">
              <Select value={connectorPolicyKind} onValueChange={(value) => setConnectorPolicyKind(value as ConnectorKind)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="bridge">bridge</SelectItem>
                  <SelectItem value="adapter">adapter</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Connector Ref">
              <Select value={connectorPolicyRef} onValueChange={setConnectorPolicyRef}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {connectorPolicyOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Policy Code">
              <Input value={connectorPolicyCode} onChange={(e) => setConnectorPolicyCode(e.target.value)} />
            </Field>
            <Field label="Policy JSON">
              <Textarea value={connectorPolicyJson} onChange={(e) => setConnectorPolicyJson(e.target.value)} rows={8} />
            </Field>
            <Field label="Notes">
              <Input value={connectorPolicyNotes} onChange={(e) => setConnectorPolicyNotes(e.target.value)} />
            </Field>
            <Button onClick={() => void createConnectorPolicy()} disabled={submitting === 'connector-policy'}>
              {submitting === 'connector-policy' && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {editingConnectorPolicyId ? 'Update Connector Policy' : 'Save Connector Policy'}
            </Button>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 xl:grid-cols-3">
        <PolicyTable
          title="Bindings"
          rows={overview.bindings}
          columns={['skill_id', 'connector_kind', 'connector_ref', 'binding_role', 'priority']}
          onEdit={(row) => startEditBinding(row)}
          onDelete={(id) => void deleteRecord('bindings', id)}
          busyId={submitting}
        />
        <PolicyTable
          title="Skill Policies"
          rows={overview.skillPolicies}
          columns={['skill_id', 'policy_scope', 'status', 'version_no']}
          onEdit={(row) => startEditSkillPolicy(row)}
          onDelete={(id) => void deleteRecord('skill-policies', id)}
          busyId={submitting}
        />
        <PolicyTable
          title="Connector Policies"
          rows={overview.connectorPolicies}
          columns={['connector_kind', 'connector_ref', 'policy_code', 'status']}
          onEdit={(row) => startEditConnectorPolicy(row)}
          onDelete={(id) => void deleteRecord('connector-policies', id)}
          busyId={submitting}
        />
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      {children}
    </div>
  );
}

function PolicyTable({
  title,
  rows,
  columns,
  onEdit,
  onDelete,
  busyId,
}: {
  title: string;
  rows: Array<Record<string, unknown>>;
  columns: string[];
  onEdit: (row: Record<string, unknown>) => void;
  onDelete: (id: string) => void;
  busyId: string | null;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Network className="h-4 w-4" />
          {title}
          <Badge variant="outline">{rows.length}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              {columns.map((column) => (
                <TableHead key={column}>{column}</TableHead>
              ))}
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length ? rows.map((row) => {
              const id = asString(row['id'], '');
              return (
                <TableRow key={id}>
                  {columns.map((column) => (
                    <TableCell key={column} className="text-xs">
                      {asString(row[column])}
                    </TableCell>
                  ))}
                  <TableCell className="w-[88px]">
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="icon" onClick={() => onEdit(row)}>
                        <Pencil className="h-4 w-4 text-sky-600" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => onDelete(id)} disabled={busyId === `delete-${id}`}>
                        {busyId === `delete-${id}` ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4 text-red-600" />}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            }) : (
              <TableRow>
                <TableCell colSpan={columns.length + 1} className="text-sm text-muted-foreground">
                  No records yet.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
